import type { SshKey } from "@cloudserver/contracts";
import { Copy, KeyRound, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useCanWrite, useReadOnly } from "../api/users.js";
import { useCreateSshKey, useDeleteSshKey, useRenameSshKey, useSshKeys } from "../api/queries.js";
import { DangerDialog } from "../ui/confirm.js";
import { ErrorMessage, fieldError, problemText } from "../ui/feedback.js";
import { relativeTime } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Dialog } from "../ui/overlay.js";
import { Alert, Button, TextArea, TextField } from "../ui/primitives.js";
import { DataTable } from "../ui/table.js";
import { useToast } from "../ui/toast.js";
import { ApiProblem } from "../api/client.js";

/** A key's name from the comment at the end of the line (`… anna@laptop`), if any. */
export function nameFromKey(publicKey: string): string {
  const parts = publicKey.trim().split(/\s+/);
  return parts.length > 2 ? parts.slice(2).join(" ").slice(0, 128) : "";
}

export function AddKeyDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateSshKey(projectId);
  const toast = useToast();
  const [publicKey, setPublicKey] = useState("");
  const [name, setName] = useState("");
  const file = useRef<HTMLInputElement>(null);

  const close = (next: boolean) => {
    if (!next) {
      setPublicKey("");
      setName("");
      create.reset();
    }
    onOpenChange(next);
  };
  const applyPublicKey = (value: string) => {
    setPublicKey(value);
    if (!name) setName(nameFromKey(value));
  };
  const readFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    if (chosen) applyPublicKey((await chosen.text()).trim());
    event.target.value = "";
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    create.mutate(
      { name: name.trim(), publicKey: publicKey.trim() },
      {
        onSuccess: (key) => {
          toast({ tone: "ok", title: t("sshKeys.added", { name: key.name }) });
          close(false);
        },
      },
    );
  };
  const looksPrivate = /PRIVATE KEY/.test(publicKey);

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={t("sshKeys.add")}
      description={t("sshKeys.addLead")}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="add-ssh-key" busy={create.isPending} disabled={looksPrivate}>
            {t("sshKeys.submit")}
          </Button>
        </>
      }
    >
      <form id="add-ssh-key" onSubmit={submit} className="flex flex-col gap-4">
        <TextArea
          label={t("sshKeys.publicKey")}
          name="publicKey"
          value={publicKey}
          onChange={(e) => applyPublicKey(e.target.value)}
          required
          rows={4}
          spellCheck={false}
          placeholder="ssh-ed25519 AAAA… user@laptop"
          hint={t("sshKeys.publicKeyHint")}
          info={<FieldHint text={t("sshKeys.publicKeyWhy")} topic="ssh-keys" />}
          error={
            looksPrivate
              ? t("sshKeys.privateKey")
              : (fieldError(create.error, "publicKey", t) ?? providerKeyError(create.error, t))
          }
        />
        <div>
          <input
            ref={file}
            type="file"
            accept=".pub,text/plain"
            className="hidden"
            onChange={(e) => void readFile(e)}
          />
          <Button
            variant="secondary"
            icon={<Upload className="size-4" aria-hidden />}
            onClick={() => file.current?.click()}
          >
            {t("sshKeys.fromFile")}
          </Button>
        </div>
        <TextField
          label={t("sshKeys.name")}
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={128}
          hint={t("sshKeys.nameHint")}
          error={fieldError(create.error, "name", t)}
        />
        {!providerKeyError(create.error, t) && <ErrorMessage error={create.error} />}
      </form>
    </Dialog>
  );
}

/** The provider's verdict on the key itself (invalid or already added) belongs under the key field. */
function providerKeyError(
  error: unknown,
  t: Parameters<typeof problemText>[0],
): string | undefined {
  if (!(error instanceof ApiProblem) || !error.problem) return undefined;
  const { reason } = error.problem;
  if (reason === "provider_invalid_input") return t("sshKeys.invalid");
  if (reason === "provider_conflict") return t("sshKeys.duplicate");
  return undefined;
}

function RenameDialog({
  projectId,
  sshKey,
  onClose,
}: {
  projectId: string;
  sshKey: SshKey;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const rename = useRenameSshKey(projectId);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get("name") ?? "").trim();
    rename.mutate({ id: sshKey.id, name }, { onSuccess: onClose });
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("sshKeys.rename")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="rename-ssh-key" busy={rename.isPending}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="rename-ssh-key" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("sshKeys.name")}
          name="name"
          defaultValue={sshKey.name}
          required
          maxLength={128}
          error={fieldError(rename.error, "name", t)}
        />
        <ErrorMessage error={rename.error} />
      </form>
    </Dialog>
  );
}

export function SshKeysPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  // Guests may only look (docs/users.md): no create action; the API refuses them anyway.
  const canWrite = useCanWrite(projectId);
  const keys = useSshKeys(projectId);
  const remove = useDeleteSshKey(projectId);
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<SshKey | null>(null);
  const [deleting, setDeleting] = useState<SshKey | null>(null);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);

  const addButton = (
    <Button
      onClick={() => setAdding(true)}
      disabled={readOnly}
      icon={<Plus className="size-4" aria-hidden />}
    >
      {t("sshKeys.add")}
    </Button>
  );
  const copy = async (key: SshKey) => {
    await navigator.clipboard.writeText(key.publicKey);
    toast({ tone: "ok", title: t("sshKeys.copied") });
  };

  return (
    <>
      <PageHeader
        title={t("nav.sshKeys")}
        intro={t("sshKeys.intro")}
        help="ssh-keys"
        actions={canWrite && keys.data?.length ? addButton : undefined}
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      {keys.error ? (
        <ErrorMessage error={keys.error} />
      ) : (
        <DataTable<SshKey>
          label={t("nav.sshKeys")}
          rows={keys.data}
          rowKey={(k) => k.id}
          initialSort={{ key: "name", dir: "asc" }}
          empty={
            <EmptyState
              icon={<KeyRound className="size-6" aria-hidden />}
              title={t("sshKeys.empty")}
              action={canWrite ? addButton : undefined}
              help="ssh-keys"
            >
              {t("sshKeys.emptyLead")}
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              header: t("sshKeys.name"),
              primary: true,
              sort: (k) => k.name.toLowerCase(),
              cell: (k) => <span className="font-medium">{k.name}</span>,
            },
            {
              key: "fingerprint",
              header: (
                <span className="inline-flex items-center gap-1">
                  {t("sshKeys.fingerprint")}
                  <FieldHint text={t("sshKeys.fingerprintHint")} />
                </span>
              ),
              cell: (k) => (
                <span className="break-all font-mono text-xs text-muted">{k.fingerprint}</span>
              ),
            },
            {
              key: "type",
              header: t("sshKeys.type"),
              hideOnPhone: true,
              sort: (k) => k.publicKey.split(" ")[0] ?? "",
              cell: (k) => <span className="text-muted">{k.publicKey.split(" ")[0]}</span>,
            },
            {
              key: "created",
              header: t("sshKeys.created"),
              sort: (k) => k.createdAt,
              cell: (k) => (
                <span className="text-muted">{relativeTime(k.createdAt, i18n.language)}</span>
              ),
            },
          ]}
          rowMenu={(k) => [
            [
              {
                label: t("sshKeys.copy"),
                icon: <Copy className="size-4" aria-hidden />,
                onSelect: () => void copy(k),
              },
              {
                label: t("sshKeys.rename"),
                icon: <Pencil className="size-4" aria-hidden />,
                disabled: readOnly,
                reason: readOnlyReason,
                onSelect: () => setRenaming(k),
              },
            ],
            [
              {
                label: t("sshKeys.delete"),
                icon: <Trash2 className="size-4" aria-hidden />,
                danger: true,
                disabled: readOnly,
                reason: readOnlyReason,
                onSelect: () => setDeleting(k),
              },
            ],
          ]}
        />
      )}
      <AddKeyDialog projectId={projectId} open={adding} onOpenChange={setAdding} />
      {renaming && (
        <RenameDialog projectId={projectId} sshKey={renaming} onClose={() => setRenaming(null)} />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t("sshKeys.deleteTitle", { name: deleting?.name ?? "" })}
        consequences={[t("sshKeys.deleteNew"), t("sshKeys.deleteExisting")]}
        actionLabel={t("sshKeys.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("sshKeys.deleted", { name: deleting.name }) });
              setDeleting(null);
            },
            onError: (error) =>
              toast({
                tone: "danger",
                title:
                  error instanceof ApiProblem && error.problem
                    ? problemText(t, error.problem)
                    : t("common.unexpected"),
              }),
          })
        }
      />
    </>
  );
}
